import { TranslateService } from '@ngx-translate/core';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';

export enum AppLanguage {
  Russian = 'ru',
  English = 'en',
  Uzbek = 'uz',
  France = 'fr',
}

export enum TuiLanguage {
  Russian = 'russian',
  English = 'english',
  France = 'french',
}

export const SUPPORTED_LANGUAGES: readonly AppLanguage[] = [
  AppLanguage.English,
  AppLanguage.Russian,
  AppLanguage.Uzbek,
  AppLanguage.France,
] as const;

export const LANGUAGE_NAMES: Record<AppLanguage, string> = {
  [AppLanguage.Russian]: 'Русский',
  [AppLanguage.English]: 'English',
  [AppLanguage.Uzbek]: "O'zbek",
  [AppLanguage.France]: 'Français',
};

export const isValidAppLanguage = (lang: string | null | undefined): lang is AppLanguage => {
  return lang !== null && lang !== undefined && SUPPORTED_LANGUAGES.includes(lang as AppLanguage);
};

export const getBrowserLang = (): string => {
  if (navigator.languages != undefined) return navigator.languages[0];
  return navigator.language;
};

export const mapLanguageToTui = (lang: string): TuiLanguage => {
  switch (lang) {
    case AppLanguage.Russian:
      return TuiLanguage.Russian;
    case AppLanguage.Uzbek:
      return TuiLanguage.English;
    case AppLanguage.France:
      return TuiLanguage.France;
    case AppLanguage.English:
    default:
      return TuiLanguage.English;
  }
};

export const mapLanguageToCountryIsoCode = (lang: string): TuiCountryIsoCode => {
  switch (lang) {
    case AppLanguage.Russian:
      return 'RU';
    case AppLanguage.Uzbek:
      return 'UZ';
    case AppLanguage.France:
      return 'FR';
    case AppLanguage.English:
    default:
      return 'GB';
  }
};

export const appStoreIcon: Record<AppLanguage, string> = {
  [AppLanguage.Russian]: 'common/assets/icons/marketLinkIcons/appStoreRU.svg',
  [AppLanguage.English]: 'common/assets/icons/marketLinkIcons/appStoreENG.svg',
  [AppLanguage.Uzbek]: 'common/assets/icons/marketLinkIcons/appStoreENG.svg',
  [AppLanguage.France]: 'common/assets/icons/marketLinkIcons/appStoreENG.svg',
} as const;

export const googlePlayIcon: Record<AppLanguage, string> = {
  [AppLanguage.Russian]: 'common/assets/icons/marketLinkIcons/googlePlayRU.svg',
  [AppLanguage.English]: 'common/assets/icons/marketLinkIcons/googlePlayENG.svg',
  [AppLanguage.Uzbek]: 'common/assets/icons/marketLinkIcons/googlePlayENG.svg',
  [AppLanguage.France]: 'common/assets/icons/marketLinkIcons/googlePlayENG.svg',
} as const;

export const initializeLanguage = (
  translate: TranslateService,
  defaultLocale: AppLanguage,
  availableLocales: readonly AppLanguage[] = SUPPORTED_LANGUAGES,
): AppLanguage => {
  const localStorageLanguage = localStorage.getItem('lang');
  const browserLanguage = translate.getBrowserLang();

  let selectedLanguage: AppLanguage;

  if (isValidAppLanguage(localStorageLanguage) && availableLocales.includes(localStorageLanguage)) {
    selectedLanguage = localStorageLanguage;
  } else if (isValidAppLanguage(browserLanguage) && availableLocales.includes(browserLanguage)) {
    selectedLanguage = browserLanguage;
  } else {
    selectedLanguage = defaultLocale;
  }

  translate.use(selectedLanguage);

  return selectedLanguage;
};

export const saveLanguageToStorage = (language: AppLanguage): void => {
  localStorage.setItem('lang', language);
};

export const getLanguageSelectOptions = (): readonly { id: AppLanguage; title: string }[] => {
  return SUPPORTED_LANGUAGES.map((lang) => ({
    id: lang,
    title: LANGUAGE_NAMES[lang],
  }));
};
